import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  CalendarDays,
  CalendarRange,
  Clock,
  FileCheck2,
  LayoutDashboard,
  LogOut,
  UserRound,
  Users,
} from "lucide-react";

import { GeoKerjaLogo } from "@/components/GeoKerjaLogo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/auth/AuthContext";
import { useEmployeeUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { cn } from "@/lib/utils";
import { AuthSplash } from "./AuthSplash";

/**
 * The employee lane (FEAT-2026-013) — the first web surface the EMPLOYEE
 * persona has had.
 *
 * Deliberately not the admin shell: no sidebar, no dense navigation, two
 * destinations. Someone clocking in wants one button, and often on a phone
 * browser, so the layout stays single-column at every width.
 *
 * Gating is on `employee`, never on `user`. An employee normally has no
 * `User` row at all, so any check that went through the admin principal would
 * lock out exactly the people this lane exists for.
 */
/**
 * Five destinations (six for someone with direct reports), still not a sidebar. The lane grew to match what the
 * mobile app offers (schedule, requests, profile) because a web-only employee
 * otherwise had no way to apply for leave or WFA, see their roster, or set the
 * home location that WFH classification depends on — all of which the app has
 * had since launch.
 *
 * It scrolls horizontally rather than wrapping: this is used one-handed on a
 * phone browser, and a nav that reflows to two rows pushes the check-in button
 * below the fold.
 */
const NAV = [
  { to: "/me", end: true, label: "Today", icon: Clock },
  { to: "/me/attendance", end: false, label: "Attendance", icon: CalendarDays },
  { to: "/me/schedule", end: false, label: "Schedule", icon: CalendarRange },
  { to: "/me/requests", end: false, label: "Requests", icon: FileCheck2 },
  { to: "/me/profile", end: false, label: "Profile", icon: UserRound },
];

/** Shown only to someone with direct reports (BR-206, FEAT-2026-026). */
const TEAM_NAV = { to: "/me/team", end: false, label: "My team", icon: Users };


export function EmployeeLayout() {
  const { employee, user, loading, linkError } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const unread = useEmployeeUnreadNotifications();

  if (loading) return <AuthSplash />;
  if (!employee) {
    // An admin who is not also an employee has no business here — send them to
    // their own lane rather than showing an error. Anyone else goes to login.
    if (user) return <Navigate to="/" replace />;
    return <Navigate to="/login" replace state={{ from: location, linkError }} />;
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background">
      <header className="sticky top-0 z-10 border-b bg-card">
        <div className="mx-auto flex w-full max-w-3xl items-center gap-3 px-4 py-3">
          <GeoKerjaLogo subtitle="Employee Portal" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium leading-tight">{employee.name}</p>
            <p className="truncate text-xs text-muted-foreground leading-tight">
              {employee.empCode} · {employee.tenantName}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="relative shrink-0"
            aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
            onClick={() => navigate("/me/notifications")}
          >
            <Bell className="h-4 w-4" />
            {unread > 0 && (
              <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-semibold text-destructive-foreground">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </Button>
          {/* A dual-role human lands in the admin lane by default; this is how
              they get back to it without retyping a URL. */}
          {user && (
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
              <LayoutDashboard className="mr-1.5 h-4 w-4" />
              Admin
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive"
            onClick={() => {
              logout();
              navigate("/login");
            }}
          >
            <LogOut className="mr-1.5 h-4 w-4" />
            Sign out
          </Button>
        </div>
        <nav className="mx-auto flex w-full max-w-3xl gap-1 overflow-x-auto px-4">
          {(employee.directReportCount > 0 ? [...NAV, TEAM_NAV] : NAV).map(({ to, end, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "border-primary font-medium text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto w-full max-w-3xl flex-1 p-4 animate-fade-in">
        <Outlet />
      </main>
    </div>
  );
}
